import { useRef, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { api, apiError, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { btnDark, btnPrimary, inputCls, labelCls } from "./shared";

export default function CategoriesTab({ categories, reload }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", sort_order: 0, is_active: true, parent_id: "" });
  const fileRef = useRef(null);

  const openEdit = (c) => {
    setEditing(c);
    setForm(c === "new" ? { name: "", description: "", sort_order: categories.length, is_active: true, parent_id: "" } : { name: c.name, description: c.description, sort_order: c.sort_order, is_active: c.is_active, parent_id: c.parent_id || "" });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/categories", form);
      else await api.put(`/admin/categories/${editing.id}`, form);
      toast.success("Category saved");
      setEditing(null);
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || editing === "new") return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/categories/${editing.id}/image`, fd);
      toast.success("Image updated");
      reload();
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category? Its products become uncategorised.")) return;
    await api.delete(`/admin/categories/${id}`).then(() => { toast.success("Deleted"); reload(); }).catch((e) => toast.error(apiError(e)));
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/categories/${c.id}`, { name: c.name, description: c.description, sort_order: c.sort_order, is_active: !c.is_active, parent_id: c.parent_id || "" });
      toast.success(c.is_active ? "Category hidden" : "Category enabled");
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  const move = async (c, dir) => {
    const siblings = categories.filter((x) => (x.parent_id || "") === (c.parent_id || "")).sort((a, b) => a.sort_order - b.sort_order);
    const i = siblings.findIndex((x) => x.id === c.id);
    const swap = siblings[i + dir];
    if (!swap) return;
    try {
      await api.put(`/admin/categories/${c.id}`, { name: c.name, description: c.description, sort_order: swap.sort_order, is_active: c.is_active, parent_id: c.parent_id || "" });
      await api.put(`/admin/categories/${swap.id}`, { name: swap.name, description: swap.description, sort_order: c.sort_order, is_active: swap.is_active, parent_id: swap.parent_id || "" });
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  const mains = categories.filter((c) => !c.parent_id).sort((a, b) => a.sort_order - b.sort_order);
  const rows = [];
  mains.forEach((m) => {
    rows.push(m);
    categories.filter((c) => c.parent_id === m.id).sort((a, b) => a.sort_order - b.sort_order).forEach((s) => rows.push(s));
  });
  categories.filter((c) => c.parent_id && !mains.some((m) => m.id === c.parent_id)).forEach((o) => rows.push(o));

  return (
    <div data-testid="admin-categories-tab">
      <div className="mb-5 flex justify-end">
        <button data-testid="add-category-btn" onClick={() => openEdit("new")} className={btnDark}><Plus size={14} /> New category</button>
      </div>
      {editing && (
        <form onSubmit={save} className="glass-deep mb-6 rounded-3xl p-6" data-testid="category-form">
          <div className="flex items-center justify-between">
            <p className="font-display text-lg">{editing === "new" ? "New category" : `Edit: ${editing.name}`}</p>
            <button type="button" onClick={() => setEditing(null)} aria-label="Close" data-testid="category-form-close" className="rounded-full p-1.5 hover:bg-secondary"><X size={16} /></button>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className={labelCls}>Name<input data-testid="category-name-input" required className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></label>
            <label className={labelCls}>Sort order<input data-testid="category-sort-input" type="number" className={inputCls} value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))} /></label>
            <label className={labelCls}>Parent category (optional — makes this a subcategory)
              <select data-testid="category-parent-select" className={inputCls} value={form.parent_id || ""} onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}>
                <option value="">None (main category)</option>
                {categories.filter((c) => !c.parent_id && (editing === "new" || c.id !== editing.id)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className={`${labelCls} sm:col-span-2`}>Description<textarea data-testid="category-desc-input" rows={2} className={inputCls} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></label>
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest">
              <input type="checkbox" data-testid="category-active-input" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="accent-primary" /> Active (visible on site)
            </label>
            {editing !== "new" && (
              <div className="flex items-center gap-3">
                {editing.image_url && <img src={imgUrl(editing.image_url)} alt="" className="h-14 w-14 rounded-xl object-cover" />}
                <button type="button" data-testid="category-image-upload" onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-2 rounded-full border border-input px-4 py-2 text-xs uppercase tracking-widest hover:bg-secondary"><Upload size={13} /> Image</button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="category-image-input" onChange={uploadImage} />
              </div>
            )}
          </div>
          <button type="submit" data-testid="category-save-btn" className={`${btnPrimary} mt-5`}>Save category</button>
        </form>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((c) => (
          <div key={c.id} className={`glass flex items-center justify-between rounded-2xl p-4 ${c.parent_id ? "ml-4 border-l-2 border-primary/30" : ""}`} data-testid={`admin-category-${c.slug}`}>
            <div className="flex min-w-0 items-center gap-3">
              {c.image_url ? <img src={imgUrl(c.image_url)} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" /> : <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary font-display">{c.name[0]}</span>}
              <div className="min-w-0"><p className="truncate text-sm">{c.name}</p>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.parent_id ? "subcategory · " : ""}{c.is_active ? "active" : "hidden"}</p></div>
            </div>
            <div className="flex shrink-0 gap-0.5">
              <button data-testid={`category-up-${c.slug}`} onClick={() => move(c, -1)} aria-label="Move up" className="rounded-full p-1.5 hover:bg-secondary"><ArrowUp size={13} /></button>
              <button data-testid={`category-down-${c.slug}`} onClick={() => move(c, 1)} aria-label="Move down" className="rounded-full p-1.5 hover:bg-secondary"><ArrowDown size={13} /></button>
              <button data-testid={`category-toggle-${c.slug}`} onClick={() => toggleActive(c)} aria-label="Toggle visibility" className="rounded-full p-1.5 hover:bg-secondary">{c.is_active ? <Eye size={13} /> : <EyeOff size={13} />}</button>
              <button data-testid={`category-edit-${c.slug}`} onClick={() => openEdit(c)} aria-label="Edit" className="rounded-full p-1.5 hover:bg-secondary"><Pencil size={13} /></button>
              <button data-testid={`category-delete-${c.slug}`} onClick={() => remove(c.id)} aria-label="Delete" className="rounded-full p-1.5 hover:bg-secondary hover:text-destructive"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
